import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    FilterBarToggle,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, contracts } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters(['contracts']);
    const confirm = useConfirmationStore(state => state.confirm);

    const destroy = contract => {
        router.delete(`/contracts/${contract.id}`);
    };

    const confirmDelete = contract => {
        confirm({
            description:
                'Weet u zeker dat u deze contract wilt verwijderen? Gekoppelde informatie zal ook niet meer beschikbaar zijn.',
            onConfirm: close => {
                destroy(contract);
                close();
            },
        });
    };

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Contracten (${contracts?.meta?.total || 0})`}
                >
                    {auth.permissions.includes('create contracts') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/contracts/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe contract
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['contracts']}>
                        <FilterBarToggle
                            label='Gesloten'
                            checked={filters?.closed == 1}
                            onChange={checked =>
                                updateFilters('closed', checked ? '1' : '0')
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Bedrijf</TableHeaderCell>
                                <TableHeaderCell>Beheerder</TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('created_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'created_at')
                                    }
                                >
                                    Aangemaakt op
                                </TableHeaderCell>
                                <TableHeaderCell>Verstuurd op</TableHeaderCell>
                                <TableHeaderCell>Getekend op</TableHeaderCell>
                                <TableHeaderCell />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {contracts?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={6} />
                            )}

                            {contracts?.data?.map(contract => (
                                <TableRow key={contract.id}>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update contracts'
                                        ) ? (
                                            <Link
                                                href={`/contracts/${contract.id}/edit`}
                                            >
                                                {contract.company_name}
                                            </Link>
                                        ) : (
                                            contract.company_name
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {contract?.user?.name}
                                    </TableCell>
                                    <TableCell>
                                        {localDate(contract.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        {localDate(contract.sent_at)}
                                    </TableCell>
                                    <TableCell>
                                        {localDate(contract.signed_at)}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update contracts',
                                            'delete contracts',
                                        ]?.some(permission =>
                                            auth?.permissions?.includes(
                                                permission
                                            )
                                        ) && (
                                            <Menu>
                                                <MenuButton>
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update contracts'
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                as={Link}
                                                                href={`/contracts/${contract.id}/edit`}
                                                                icon={
                                                                    PencilIcon
                                                                }
                                                            >
                                                                Wijzigen
                                                            </MenuItem>

                                                            <MenuSeparator />
                                                        </>
                                                    )}
                                                    {auth?.permissions?.includes(
                                                        'delete contracts'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            onClick={() =>
                                                                confirmDelete(
                                                                    contract
                                                                )
                                                            }
                                                            icon={TrashIcon}
                                                        >
                                                            Verwijderen
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={contracts?.meta}
                        only={['contracts']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
